if (!customElements.get('opsando-layby')) {
  const DISCOUNT_CART_KEY = 'opsando-layby-discount-cart';

  customElements.define(
    'opsando-layby',
    class OpsandoLayby extends HTMLElement {
      connectedCallback() {
        this.variants = JSON.parse(this.querySelector('[data-layby-variants]').textContent);
        this.laybyInput = this.querySelector('input[value="layby"]');
        this.buyButton = this.querySelector('[data-layby-buy]');
        this.errorMessage = this.querySelector('[data-layby-error]');

        const modal = document.getElementById(this.dataset.modalId);
        this.copyButton = modal.querySelector('[data-layby-copy]');
        this.copyAction = this.copyButton.querySelector('.layby-modal__code-action');
        this.copyActionText = this.copyAction.textContent;
        this.amountNodes = [...new Set([...this.querySelectorAll('[data-layby-amount]'), ...modal.querySelectorAll('[data-layby-amount]')])];

        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.addEventListener('change', (event) => this.onOptionChange(event), { signal });
        this.buyButton.addEventListener('click', () => this.checkout(this.mainForm, this.buyButton), { signal });
        this.copyButton.addEventListener('click', () => this.copyCode(), { signal });
        document.addEventListener('submit', (event) => this.onSubmit(event), { capture: true, signal });
        document.addEventListener('change', (event) => this.onVariantChange(event), { signal });
        window.addEventListener('pageshow', (event) => this.onPageShow(event), { signal });

        this.syncPaymentButtons();
      }

      disconnectedCallback() {
        this.abortController.abort();
        clearTimeout(this.copyTimer);
      }

      get mainForm() {
        return document.getElementById(`product-form-${this.dataset.sectionId}`);
      }

      get stickyForm() {
        return document.querySelector('sticky-atc-bar form[data-type="add-to-cart-form"]');
      }

      onOptionChange(event) {
        if (event.target.name !== this.laybyInput.name) return;
        this.errorMessage.hidden = true;
        this.syncPaymentButtons();
        if (!this.laybyInput.checked) this.clearDiscount();
      }

      onVariantChange(event) {
        if (event.target.name === 'id' && event.target.form === this.mainForm) this.renderVariant(event.target.value);
      }

      onSubmit(event) {
        const form = event.target;
        if (!this.laybyInput.checked || (form !== this.mainForm && form !== this.stickyForm)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.checkout(form, event.submitter || form.querySelector('[type="submit"]'));
      }

      onPageShow(event) {
        if (event.persisted && this.loadingButton) this.setLoading(this.loadingButton, false);
      }

      syncPaymentButtons() {
        const buttons = [
          this.mainForm?.querySelector('.shopify-payment-button'),
          this.stickyForm?.querySelector('.product-form__button-dynamic'),
        ];
        buttons.forEach((button) => {
          if (button) button.hidden = this.laybyInput.checked;
        });
      }

      getAmounts(price) {
        const deposit = Math.round((price * Number(this.dataset.depositPercent)) / 100);
        const interest = Math.round((price * Number(this.dataset.interestPercent)) / 100);
        const balance = price - deposit;
        return { price, deposit, interest, balance, total: balance + interest };
      }

      renderVariant(variantId) {
        const variant = this.variants[variantId];
        const teasers = document.querySelectorAll(`[data-layby-teaser="${this.dataset.sectionId}"]`);
        this.classList.toggle('layby--unavailable', !variant);
        this.buyButton.disabled = !variant || !variant.available;
        teasers.forEach((teaser) => {
          teaser.hidden = !variant || !variant.available;
        });
        if (!variant) return;

        const amounts = this.getAmounts(variant.price);
        const teaserNodes = Array.from(teasers, (teaser) => teaser.querySelector('[data-layby-amount]'));
        [...this.amountNodes, ...teaserNodes].forEach((node) => {
          node.innerHTML = FoxTheme.Currency.formatMoney(amounts[node.dataset.laybyAmount], FoxTheme.settings.moneyFormat);
        });
      }

      setLoading(button, loading) {
        button.classList.toggle('btn--loading', loading);
        if (loading) {
          button.setAttribute('aria-disabled', 'true');
          this.loadingButton = button;
        } else {
          button.removeAttribute('aria-disabled');
          this.loadingButton = null;
        }
      }

      showError(message) {
        this.errorMessage.textContent = message || this.dataset.errorMessage;
        this.errorMessage.hidden = false;
      }

      postCart(url, body) {
        return fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        }).then((response) =>
          response.json().then((json) => {
            if (!response.ok) throw json;
            return json;
          })
        );
      }

      clearDiscount() {
        let token = null;
        try {
          token = localStorage.getItem(DISCOUNT_CART_KEY);
        } catch (error) {
          return;
        }
        if (!token) return;

        fetch(`${FoxTheme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => response.json())
          .then((cart) => {
            if (cart.token === token) return this.postCart(`${FoxTheme.routes.cart_update_url}.js`, { discount: '' });
          })
          .then(() => localStorage.removeItem(DISCOUNT_CART_KEY))
          .catch(() => {});
      }

      checkout(form, button) {
        if (!form || !button) return;
        const formData = new FormData(form);
        const variantId = formData.get('id');
        const variant = this.variants[variantId];
        if (!variant || !variant.available || button.getAttribute('aria-disabled') === 'true') return;

        this.setLoading(button, true);
        this.errorMessage.hidden = true;

        this.postCart(`${FoxTheme.routes.cart_add_url}.js`, {
          items: [{ id: Number(variantId), quantity: Math.max(parseInt(formData.get('quantity'), 10) || 1, 1) }],
        })
          .then(() => this.postCart(`${FoxTheme.routes.cart_update_url}.js`, { discount: this.dataset.discountCode }))
          .then((cart) => {
            try {
              localStorage.setItem(DISCOUNT_CART_KEY, cart.token);
            } catch (error) {}
            window.location.href = this.dataset.checkoutUrl;
          })
          .catch((error) => {
            this.setLoading(button, false);
            this.showError(error && error.description);
          });
      }

      copyCode() {
        navigator.clipboard
          .writeText(this.copyButton.dataset.laybyCopy)
          .then(() => {
            clearTimeout(this.copyTimer);
            this.copyButton.classList.add('is-copied');
            this.copyAction.textContent = this.dataset.copiedMessage;
            this.copyTimer = setTimeout(() => {
              this.copyButton.classList.remove('is-copied');
              this.copyAction.textContent = this.copyActionText;
            }, 2000);
          })
          .catch(() => {});
      }
    }
  );
}
